import t from "@ext/localization/locale/translate";
import BpmnRenderError from "@ext/markdown/elements/diagrams/diagrams/bpmn/bpmnRenderError";
import getBoundsFromModel, { type Bounds } from "@ext/markdown/elements/diagrams/diagrams/bpmn/getBoundsFromModel";
import installSvgDomShim from "@ext/markdown/elements/diagrams/diagrams/bpmn/svgDomShim";

/** Палитра аргументом, не через CSS: при растеризации в PDF и DOCX `var(--token)` даёт чёрное. */
export interface BpmnPalette {
	stroke: string;
	fill: string;
	label: string;
}

export const DEFAULT_BPMN_PALETTE: BpmnPalette = {
	stroke: "#22242a",
	fill: "#ffffff",
	label: "#22242a",
};

const withCanvasSize = (svg: string, bounds: Bounds): string => {
	const viewBox = `${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`;
	return svg
		.replace(/(<svg[^>]*?)\swidth="[^"]*"/, `$1 width="${bounds.width}"`)
		.replace(/(<svg[^>]*?)\sheight="[^"]*"/, `$1 height="${bounds.height}"`)
		.replace(/(<svg[^>]*?)\sviewBox="[^"]*"/, `$1 viewBox="${viewBox}"`);
};

const getBpmnDiagram = async (diagramContent: string, palette: BpmnPalette = DEFAULT_BPMN_PALETTE): Promise<string> => {
	if (!diagramContent) throw new BpmnRenderError("no-content", t("diagram.error.cannot-get-data"));

	if (typeof document === "undefined")
		throw new BpmnRenderError(
			"dom-unavailable",
			t("diagram.error.cannot-get-data"),
			new Error("bpmn: DOM is unavailable"),
		);

	installSvgDomShim();

	const { default: Viewer } = await import("bpmn-js/lib/Viewer");

	const container = document.createElement("div");
	document.body.appendChild(container);
	const viewer = new Viewer({
		container,
		bpmnRenderer: {
			defaultStrokeColor: palette.stroke,
			defaultFillColor: palette.fill,
			defaultLabelColor: palette.label,
		},
	});

	try {
		await viewer.importXML(diagramContent);
		const { svg } = await viewer.saveSVG();
		const bounds = getBoundsFromModel(viewer.get("elementRegistry"));
		return bounds ? withCanvasSize(svg, bounds) : svg;
	} catch (error) {
		// Файл без BPMN DI — штатный отказ bpmn-js, а не сбой: рисовать нечего.
		const cause = error instanceof Error ? error.message : String(error);
		throw new BpmnRenderError("invalid-syntax", t("diagram.error.invalid-syntax"), new Error(cause));
	} finally {
		viewer.destroy();
		container.remove();
	}
};

export default getBpmnDiagram;
